import { z } from "zod";

import type { FeederResponse, SldDocument } from "../services/sld";
import {
  busKindSchema,
  deviceKindSchema,
  feederKindSchema,
  loadKindSchema,
  sourceKindSchema,
  type BusKind,
  type DeviceKind,
  type FeederKind,
  type LoadKind,
  type SourceKind,
} from "../services/sldContract";
import type { ValidationLabels } from "../utils/validationMessages";

// Draft model of the single-line diagram editor (SLD-a, A26): what the engineer is typing,
// kept apart from the components so the payload rules are tested without a browser.
//
// Every value is held as the string the user typed; a choice not made yet is "" (Choose...).
// The diagram is a set of row lists, one per element kind, and each row carries a stable id
// ("bus-3") so React keys, focus and validation messages survive a reorder. A feeder names
// its target by kind first (bus or load) and then by code, so the editor shows one target
// field; the device and the cable of a branch are grouped strings, left out of the payload
// when every field of the group is blank and sent with "" for a blank field when the group
// is partly filled, so the schema names the missing figure.
//
// One draft belongs to one project revision: it remembers the revision id and the server
// version it started from (baseVersion), so the editor can tell that the diagram changed on
// the server since the draft was taken (isStale) and can send the version with the save.

/** The module key prefix of the draft in the browser tab (see useStudyDraft). */
export const SLD_DRAFT_MODULE_PREFIX = "sld:";

/** The module key of one revision's draft: the storage key carries the revision id. */
export function sldDraftModule(revisionId: string): string {
  return `${SLD_DRAFT_MODULE_PREFIX}${revisionId}`;
}

export type SldTargetKind = "" | "bus" | "load";

export type DeviceDraft = { code: string; kind: string; rating: string };
export type CableDraft = { code: string; length: string };

export type BusRow = {
  id: string;
  code: string;
  name: string;
  nominalVoltageV: string;
  kind: string;
};
export type SourceRow = {
  id: string;
  code: string;
  kind: string;
  voltageV: string;
  /** kVA of a generator or a UPS, AC kW of a PV source; optional, blank for a grid source. */
  rating: string;
};
export type TransformerRow = {
  id: string;
  code: string;
  fromBus: string;
  toBus: string;
  ratingKva: string;
  primaryV: string;
  secondaryV: string;
  /** The secondary incomer breaker at the to_bus side, optional like an incomer's device. */
  device: DeviceDraft;
};
export type FeederRow = {
  id: string;
  code: string;
  kind: string;
  fromBus: string;
  targetKind: SldTargetKind;
  toBus: string;
  toLoad: string;
  device: DeviceDraft;
  cable: CableDraft;
};
export type IncomerRow = {
  id: string;
  code: string;
  source: string;
  toBus: string;
  device: DeviceDraft;
};
export type LoadRow = { id: string; code: string; kind: string; ratedKw: string };

export type SldDraft = {
  revisionId: string;
  baseVersion: number;
  code: string;
  name: string;
  buses: BusRow[];
  sources: SourceRow[];
  transformers: TransformerRow[];
  feeders: FeederRow[];
  incomers: IncomerRow[];
  loads: LoadRow[];
  notes: string;
};

export type SldRowKind = "bus" | "source" | "transformer" | "feeder" | "incomer" | "load";

// A choice the user has not made yet is held as "".
function chosenOrBlank<T extends z.ZodType<string>>(choice: T) {
  return z.union([z.literal(""), choice]);
}

const rowId = z.string().min(1);
const deviceDraftSchema = z
  .object({ code: z.string(), kind: chosenOrBlank(deviceKindSchema), rating: z.string() })
  .strict();
const cableDraftSchema = z.object({ code: z.string(), length: z.string() }).strict();

// The shape a draft kept in the browser tab must still have to be restored: exactly the
// fields above, typed text as strings and the choices as values the contract knows or "".
// A draft saved by an older editor fails here and is dropped instead of half-filling this one.
export const sldDraftSchema = z
  .object({
    revisionId: z.string(),
    baseVersion: z.number().int().nonnegative(),
    code: z.string(),
    name: z.string(),
    buses: z.array(
      z
        .object({
          id: rowId,
          code: z.string(),
          name: z.string(),
          nominalVoltageV: z.string(),
          kind: chosenOrBlank(busKindSchema),
        })
        .strict(),
    ),
    sources: z.array(
      z
        .object({
          id: rowId,
          code: z.string(),
          kind: chosenOrBlank(sourceKindSchema),
          voltageV: z.string(),
          rating: z.string(),
        })
        .strict(),
    ),
    transformers: z.array(
      z
        .object({
          id: rowId,
          code: z.string(),
          fromBus: z.string(),
          toBus: z.string(),
          ratingKva: z.string(),
          primaryV: z.string(),
          secondaryV: z.string(),
          device: deviceDraftSchema,
        })
        .strict(),
    ),
    feeders: z.array(
      z
        .object({
          id: rowId,
          code: z.string(),
          kind: chosenOrBlank(feederKindSchema),
          fromBus: z.string(),
          targetKind: z.enum(["", "bus", "load"]),
          toBus: z.string(),
          toLoad: z.string(),
          device: deviceDraftSchema,
          cable: cableDraftSchema,
        })
        .strict(),
    ),
    incomers: z.array(
      z
        .object({
          id: rowId,
          code: z.string(),
          source: z.string(),
          toBus: z.string(),
          device: deviceDraftSchema,
        })
        .strict(),
    ),
    loads: z.array(
      z
        .object({
          id: rowId,
          code: z.string(),
          kind: chosenOrBlank(loadKindSchema),
          ratedKw: z.string(),
        })
        .strict(),
    ),
    notes: z.string(),
  })
  .strict();

// -- rows ----------------------------------------------------------------------------------

export function emptyDevice(): DeviceDraft {
  return { code: "", kind: "", rating: "" };
}

export function emptyCable(): CableDraft {
  return { code: "", length: "" };
}

/** The next free row id of a kind: "bus-1", "bus-2", ... never reusing one still in the draft. */
export function nextRowId(draft: SldDraft, kind: SldRowKind): string {
  const rows: ReadonlyArray<{ id: string }> = ROW_LISTS[kind](draft);
  const prefix = `${kind}-`;
  let highest = 0;
  for (const row of rows) {
    if (row.id.startsWith(prefix)) {
      const number = Number(row.id.slice(prefix.length));
      if (Number.isInteger(number) && number > highest) {
        highest = number;
      }
    }
  }
  return `${prefix}${highest + 1}`;
}

const ROW_LISTS: Record<SldRowKind, (draft: SldDraft) => ReadonlyArray<{ id: string }>> = {
  bus: (draft) => draft.buses,
  source: (draft) => draft.sources,
  transformer: (draft) => draft.transformers,
  feeder: (draft) => draft.feeders,
  incomer: (draft) => draft.incomers,
  load: (draft) => draft.loads,
};

export function newBusRow(id: string): BusRow {
  return { id, code: "", name: "", nominalVoltageV: "", kind: "" };
}

export function newSourceRow(id: string): SourceRow {
  return { id, code: "", kind: "", voltageV: "", rating: "" };
}

export function newTransformerRow(id: string): TransformerRow {
  return {
    id,
    code: "",
    fromBus: "",
    toBus: "",
    ratingKva: "",
    primaryV: "",
    secondaryV: "",
    device: emptyDevice(),
  };
}

export function newFeederRow(id: string): FeederRow {
  return {
    id,
    code: "",
    kind: "FEEDER",
    fromBus: "",
    targetKind: "",
    toBus: "",
    toLoad: "",
    device: emptyDevice(),
    cable: emptyCable(),
  };
}

export function newIncomerRow(id: string): IncomerRow {
  return { id, code: "", source: "", toBus: "", device: emptyDevice() };
}

export function newLoadRow(id: string): LoadRow {
  return { id, code: "", kind: "", ratedKw: "" };
}

// -- the draft and the document -------------------------------------------------------------

/** An empty draft of one revision, before any diagram was loaded or typed. */
export function createInitialSldDraft(revisionId = ""): SldDraft {
  return {
    revisionId,
    baseVersion: 0,
    code: "",
    name: "",
    buses: [],
    sources: [],
    transformers: [],
    feeders: [],
    incomers: [],
    loads: [],
    notes: "",
  };
}

function deviceFromDocument(device: FeederResponse["device"] | undefined): DeviceDraft {
  return device == null
    ? emptyDevice()
    : { code: device.code, kind: device.kind, rating: device.rating_a };
}

/** The stored diagram as the editor shows it: rows numbered from one, in the server's order. */
export function draftFromDocument(document: SldDocument): SldDraft {
  const { model } = document;
  return {
    revisionId: document.project_revision_id,
    baseVersion: document.version,
    code: model.code,
    name: model.name,
    buses: model.buses.map((bus, index) => ({
      id: `bus-${index + 1}`,
      code: bus.code,
      name: bus.name,
      nominalVoltageV: bus.nominal_voltage_v,
      kind: bus.kind,
    })),
    sources: model.sources.map((source, index) => ({
      id: `source-${index + 1}`,
      code: source.code,
      kind: source.kind,
      voltageV: source.voltage_v,
      rating: (source.kind === "PV" ? source.rating_kw : source.rating_kva) ?? "",
    })),
    transformers: model.transformers.map((transformer, index) => ({
      id: `transformer-${index + 1}`,
      code: transformer.code,
      fromBus: transformer.from_bus,
      toBus: transformer.to_bus,
      ratingKva: transformer.rating_kva,
      primaryV: transformer.primary_v,
      secondaryV: transformer.secondary_v,
      device: deviceFromDocument(transformer.device),
    })),
    feeders: model.feeders.map((feeder, index) => ({
      id: `feeder-${index + 1}`,
      code: feeder.code,
      kind: feeder.kind,
      fromBus: feeder.from_bus,
      targetKind: feeder.to_load !== null ? "load" : "bus",
      toBus: feeder.to_bus ?? "",
      toLoad: feeder.to_load ?? "",
      device: deviceFromDocument(feeder.device),
      cable:
        feeder.cable === null
          ? emptyCable()
          : { code: feeder.cable.code, length: feeder.cable.length_m },
    })),
    incomers: model.source_incomers.map((incomer, index) => ({
      id: `incomer-${index + 1}`,
      code: incomer.code,
      source: incomer.source,
      toBus: incomer.to_bus,
      device: deviceFromDocument(incomer.device),
    })),
    loads: model.loads.map((load, index) => ({
      id: `load-${index + 1}`,
      code: load.code,
      kind: load.kind,
      ratedKw: load.rated_kw,
    })),
    notes: model.notes ?? "",
  };
}

/** True when the server holds a newer version than the one the draft started from. */
export function isStale(draft: SldDraft, document: SldDocument): boolean {
  return document.version > draft.baseVersion;
}

// -- the payload ---------------------------------------------------------------------------

// A choice: sent when made, left out when blank so the schema reports it as required.
function choice(key: string, typed: string): Record<string, string> {
  const value = typed.trim();
  return value === "" ? {} : { [key]: value };
}

// A grouped block: left out when every field is blank; otherwise every field is sent, a
// blank one as "" so the schema names it.
function devicePayload(device: DeviceDraft): Record<string, unknown> {
  const code = device.code.trim();
  const rating = device.rating.trim();
  if (code === "" && device.kind === "" && rating === "") {
    return {};
  }
  return { device: { code, ...choice("kind", device.kind), rating_a: rating } };
}

// The rating of a source, under the key of its kind: kVA for a generator and a UPS, AC kW for
// a PV source. A grid source, or a kind not chosen yet, has no rating to send.
function sourceRating(source: SourceRow): Record<string, unknown> {
  const rating = source.rating.trim();
  if (rating === "") {
    return {};
  }
  if (source.kind === "PV") {
    return { rating_kw: rating };
  }
  return source.kind === "GENERATOR" || source.kind === "UPS" ? { rating_kva: rating } : {};
}

function cablePayload(cable: CableDraft): Record<string, unknown> {
  const code = cable.code.trim();
  const length = cable.length.trim();
  if (code === "" && length === "") {
    return {};
  }
  return { cable: { code, length_m: length } };
}

// The target of a feeder by the kind chosen on screen; no kind chosen sends neither end, so
// the schema reports the feeder's target as missing.
function feederTarget(feeder: FeederRow): Record<string, string> {
  if (feeder.targetKind === "bus") {
    return { to_bus: feeder.toBus.trim() };
  }
  if (feeder.targetKind === "load") {
    return { to_load: feeder.toLoad.trim() };
  }
  return {};
}

// The model as the editor will send it. It is validated afterwards with
// sldModelRequestSchema, so this function does not judge the values.
export function buildSldModel(draft: SldDraft): Record<string, unknown> {
  const notes = draft.notes.trim();
  return {
    code: draft.code.trim(),
    name: draft.name.trim(),
    buses: draft.buses.map((bus) => ({
      code: bus.code.trim(),
      name: bus.name.trim(),
      nominal_voltage_v: bus.nominalVoltageV.trim(),
      ...choice("kind", bus.kind),
    })),
    sources: draft.sources.map((source) => ({
      code: source.code.trim(),
      ...choice("kind", source.kind),
      voltage_v: source.voltageV.trim(),
      ...sourceRating(source),
    })),
    transformers: draft.transformers.map((transformer) => ({
      code: transformer.code.trim(),
      from_bus: transformer.fromBus.trim(),
      to_bus: transformer.toBus.trim(),
      rating_kva: transformer.ratingKva.trim(),
      primary_v: transformer.primaryV.trim(),
      secondary_v: transformer.secondaryV.trim(),
      ...devicePayload(transformer.device),
    })),
    feeders: draft.feeders.map((feeder) => ({
      code: feeder.code.trim(),
      ...choice("kind", feeder.kind),
      from_bus: feeder.fromBus.trim(),
      ...feederTarget(feeder),
      ...devicePayload(feeder.device),
      ...cablePayload(feeder.cable),
    })),
    source_incomers: draft.incomers.map((incomer) => ({
      code: incomer.code.trim(),
      source: incomer.source.trim(),
      to_bus: incomer.toBus.trim(),
      ...devicePayload(incomer.device),
    })),
    loads: draft.loads.map((load) => ({
      code: load.code.trim(),
      ...choice("kind", load.kind),
      rated_kw: load.ratedKw.trim(),
    })),
    ...(notes === "" ? {} : { notes }),
  };
}

// -- labels --------------------------------------------------------------------------------

// On-screen labels of the editor, keyed by the model path with row indexes as "*"; the
// groups give each list its singular name, so a message reads "Bus 2 — Nominal voltage (V)
// is required." The transformer symbol carries no device: a transformer has no device path.
export const SLD_LABELS: ValidationLabels = {
  groups: {
    buses: "Bus",
    sources: "Source",
    transformers: "Transformer",
    feeders: "Feeder",
    source_incomers: "Incomer",
    loads: "Load",
  },
  fields: {
    code: "Diagram code",
    name: "Diagram name",
    notes: "Diagram notes",
    buses: "Buses",
    sources: "Sources",
    transformers: "Transformers",
    feeders: "Feeders",
    source_incomers: "Incomers",
    loads: "Loads",
    "buses.*.code": "Code",
    "buses.*.name": "Name",
    "buses.*.nominal_voltage_v": "Nominal voltage (V)",
    "buses.*.kind": "Kind",
    "sources.*.code": "Code",
    "sources.*.kind": "Kind",
    "sources.*.voltage_v": "Voltage (V)",
    "sources.*.rating_kva": "Rating (kVA)",
    "sources.*.rating_kw": "Rating (kW AC)",
    "transformers.*.code": "Code",
    "transformers.*.from_bus": "From bus",
    "transformers.*.to_bus": "To bus",
    "transformers.*.rating_kva": "Rating (kVA)",
    "transformers.*.primary_v": "Primary voltage (V)",
    "transformers.*.secondary_v": "Secondary voltage (V)",
    "transformers.*.device": "Device",
    "transformers.*.device.code": "Device code",
    "transformers.*.device.kind": "Device kind",
    "transformers.*.device.rating_a": "Device rating (A)",
    "feeders.*.code": "Code",
    "feeders.*.kind": "Kind",
    "feeders.*.from_bus": "From bus",
    "feeders.*.to_bus": "Target bus",
    "feeders.*.to_load": "Target load",
    "feeders.*.device": "Device",
    "feeders.*.device.code": "Device code",
    "feeders.*.device.kind": "Device kind",
    "feeders.*.device.rating_a": "Device rating (A)",
    "feeders.*.cable": "Cable",
    "feeders.*.cable.code": "Cable code",
    "feeders.*.cable.length_m": "Cable length (m)",
    "source_incomers.*.code": "Code",
    "source_incomers.*.source": "Source",
    "source_incomers.*.to_bus": "To bus",
    "source_incomers.*.device": "Device",
    "source_incomers.*.device.code": "Device code",
    "source_incomers.*.device.kind": "Device kind",
    "source_incomers.*.device.rating_a": "Device rating (A)",
    "loads.*.code": "Code",
    "loads.*.kind": "Kind",
    "loads.*.rated_kw": "Rated power (kW)",
  },
};

// The words of every choice the editor offers, typed against the contract enums so a value
// the contract gains cannot pass without a label.

export const BUS_KIND_LABELS: Record<BusKind, string> = {
  HT_BUS: "HT bus",
  PCC: "PCC",
  MCC: "MCC",
  PDB: "PDB",
  DB: "DB",
};

export const SOURCE_KIND_LABELS: Record<SourceKind, string> = {
  GRID: "Grid",
  GENERATOR: "Generator",
  UPS: "UPS",
  PV: "PV",
};

export const FEEDER_KIND_LABELS: Record<FeederKind, string> = {
  FEEDER: "Feeder",
  BUSBAR_TRUNKING: "Busbar trunking",
};

export const DEVICE_KIND_LABELS: Record<DeviceKind, string> = {
  ACB: "ACB",
  MCCB: "MCCB",
  MCB: "MCB",
  FUSE: "Fuse",
};

export const LOAD_KIND_LABELS: Record<LoadKind, string> = {
  MOTOR: "Motor",
  OTHER: "Other",
};

export const TARGET_KIND_LABELS: Record<Exclude<SldTargetKind, "">, string> = {
  bus: "Bus",
  load: "Load",
};
